'use client';

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
} from 'react';
import Dialog from '@/components/Dialog';

type UseConfirmDialogOptions = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
};

type ConfirmDialogContextType = {
  confirm: (options?: UseConfirmDialogOptions) => Promise<boolean>;
};

const ConfirmDialogContext = createContext<ConfirmDialogContextType | undefined>(undefined);

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<UseConfirmDialogOptions>({});
  const [resolve, setResolve] = useState<((value: boolean) => void) | null>(null);

  const confirm = (opts: UseConfirmDialogOptions = {}): Promise<boolean> => {
    return new Promise((resolve) => {
      setOptions({
        title: 'Confirm',
        message: 'Are you sure?',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        ...opts,
      });
      setResolve(() => resolve);
      setIsOpen(true);
    });
  };

  const handleConfirm = () => {
    resolve?.(true);
    setIsOpen(false);
    setResolve(null);
  };

  const handleCancel = () => {
    resolve?.(false);
    setIsOpen(false);
    setResolve(null);
  };

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <Dialog
        open={isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={options.title}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        showActions={true}
        disableBackdropClick={true}
      >
        <p className="text-gray-600">
          {options.message}
        </p>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);
  if (context === undefined) {
    throw new Error('useConfirmDialog must be used within a ConfirmDialogProvider');
  }
  return context;
}